"use client";

import { PageTransitionLoader } from "@/components/layout/PageTransitionLoader";
import React, { createContext, useState, useContext, useCallback, PropsWithChildren } from "react";
import { useNavigate } from "react-router-dom";

interface TransitionContextType {
  isLoading: boolean;
  showLoader: (callback: () => void) => void;
}

const TransitionContext = createContext<TransitionContextType | undefined>(undefined);

export const useTransitionLoader = () => {
  const context = useContext(TransitionContext);
  if (!context) {
    throw new Error("useTransitionLoader must be used within a TransitionProvider");
  }
  return context;
};

export function TransitionProvider({ children }: PropsWithChildren) {
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const showLoader = useCallback((callback: () => void) => {
    setIsLoading(true);
    setTimeout(() => {
      callback();
      // Pequeño delay para asegurar que la navegación ocurra después de que el loader sea visible
      setTimeout(() => setIsLoading(false), 100); 
    }, 1000); // Duración de la animación del loader
  }, []);

  return (
    <TransitionContext.Provider value={{ isLoading, showLoader }}>
      <PageTransitionLoader isLoading={isLoading} />
      {children}
    </TransitionContext.Provider>
  );
}

// Componente Wrapper para enlaces de navegación
export const TransitionLink = ({ to, children, className, ...props }: { to: string, children: React.ReactNode } & React.AnchorHTMLAttributes<HTMLAnchorElement>) => {
  const { showLoader } = useTransitionLoader();
  const navigate = useNavigate();

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    showLoader(() => navigate(to));
  };

  return (
    <a href={to} onClick={handleClick} className={className} {...props}>
      {children}
    </a>
  );
};
